import React, { useState, useEffect, useCallback } from 'react';
import Modal, { ModalActions, ModalContent } from '@sportnet/ui/Modal';
import Button from '@sportnet/ui/Button';
import Header from '@sportnet/ui/Header';
import BasicTable from '@sportnet/ui/BasicTable';
import Segment from '@sportnet/ui/Segment';
import SegmentHeader from '@sportnet/ui/Segment/Header';
import Loader from '@sportnet/ui/Loader';
import { formatPrice } from '@sportnet/utilities';
import styled from 'styled-components';
import moment from 'moment';
import Api from '../../../Api';
import { __ } from '../../../utilities';
import {
  downloadInvoicePdf,
  getInvoiceTotal,
  getInvoiceAmountToPay,
} from '../helpers';

const StretchedSegment = styled(Segment)`
  flex: 1;
  margin-bottom: 0;
`;

const InvoiceDetailModal = ({
  isOpen,
  handleClose,
  invoice: initialInvoice,
  appSpace,
  connections,
}) => {
  const [exporting, setExporting] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState({});

  const fetchDetail = useCallback(async () => {
    if (!initialInvoice) return;
    setLoading(true);
    try {
      const detail = await Api.getInvoiceDetail(
        appSpace,
        initialInvoice.invoicenr,
      );
      setInvoice(detail);
    } catch (e) {
      console.error(e);
      // fallback to list data
      setInvoice(initialInvoice);
    } finally {
      setLoading(false);
    }
  }, [appSpace, initialInvoice]);

  useEffect(() => {
    if (isOpen && initialInvoice) {
      setExpandedErrors({});
      fetchDetail();
    } else {
      setInvoice(null);
    }
  }, [isOpen, initialInvoice, fetchDetail]);

  if (!isOpen) return null;

  const handleExport = async service => {
    if (
      !window.confirm(
        __('Prajete si exportovať faktúru do služby') + ` ${service}?`,
      )
    ) {
      return;
    }
    setExporting(service);
    try {
      await Api.exportInvoice(appSpace, invoice.invoicenr, service);
      alert(__('Export bol úspešný'));
      fetchDetail();
    } catch (e) {
      console.error(e);
      if (
        e.name === 'EXPORT_ERROR' ||
        (e.details && e.details.name === 'EXPORT_ERROR')
      ) {
        alert(__('Export sa nepodaril.'));
        fetchDetail();
      } else {
        alert(__('Ľutujeme, nastala neočakávaná chyba pri exporte.'));
      }
    } finally {
      setExporting(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadInvoicePdf(appSpace, invoice.invoicenr);
    } catch (e) {
      console.error(e);
      alert(__('Ľutujeme, nastala neočakávaná chyba.'));
    } finally {
      setDownloading(false);
    }
  };

  const activeConnections = (connections || []).filter(
    c => c.status === 'active',
  );

  const toggleError = key => {
    setExpandedErrors(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <ModalContent>
        {loading || !invoice ? (
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 32 }}
          >
            <Loader size="xl" />
          </div>
        ) : (
          <>
            <Header size="s" withSeparator>
              {__('Faktúra')} {invoice.invoicenr}
            </Header>

            <div style={{ marginBottom: 16 }}>
              <div>
                <strong>{__('Dátum zdaniteľného plnenia')}:</strong>{' '}
                {moment(invoice.deliveryDate).format('DD.MM.YYYY')}
              </div>
              <div>
                <strong>{__('Dátum splatnosti')}:</strong>{' '}
                {moment(invoice.payDueDate).format('DD.MM.YYYY')}
              </div>
              {invoice.storno && (
                <div style={{ color: 'red', marginTop: 4 }}>
                  <strong>STORNO:</strong>{' '}
                  {moment(invoice.stornoDate).format('DD.MM.YYYY HH:mm:ss')}
                  {invoice.stornoBy && `, ${invoice.stornoBy.displayName}`}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: '50%', minWidth: 0, display: 'flex' }}>
                <StretchedSegment
                  raised
                  header={
                    <SegmentHeader size="xs">{__('Dodávateľ')}</SegmentHeader>
                  }
                >
                  {invoice.seller && (
                    <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                      <strong>
                        {invoice.seller.full_name || invoice.seller.name}
                      </strong>
                      {invoice.seller.business_id && (
                        <div>
                          {__('IČO')}: {invoice.seller.business_id}
                        </div>
                      )}
                      {invoice.seller.vat_nr && (
                        <div>
                          {__('DIČ')}: {invoice.seller.vat_nr}
                        </div>
                      )}
                      {invoice.seller.email && (
                        <div>{invoice.seller.email}</div>
                      )}
                      {invoice.seller.phone && (
                        <div>{invoice.seller.phone}</div>
                      )}
                    </div>
                  )}
                </StretchedSegment>
              </div>
              <div style={{ width: '50%', minWidth: 0, display: 'flex' }}>
                <StretchedSegment
                  raised
                  header={
                    <SegmentHeader size="xs">{__('Odberateľ')}</SegmentHeader>
                  }
                >
                  {invoice.buyer && (
                    <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                      <strong>
                        {[invoice.buyer.name, invoice.buyer.surname]
                          .filter(Boolean)
                          .join(' ')}
                      </strong>
                      {invoice.buyer.invoice_address && (
                        <>
                          {invoice.buyer.invoice_address.address && (
                            <div>{invoice.buyer.invoice_address.address}</div>
                          )}
                          {(invoice.buyer.invoice_address.zip ||
                            invoice.buyer.invoice_address.city) && (
                            <div>
                              {[
                                invoice.buyer.invoice_address.zip,
                                invoice.buyer.invoice_address.city,
                              ]
                                .filter(Boolean)
                                .join(' ')}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </StretchedSegment>
              </div>
            </div>

            <div style={{ marginBottom: 16 }} />

            <BasicTable
              columns={[
                { header: __('Položka') },
                { header: __('Množstvo') },
                { header: __('Cena bez DPH') },
                { header: __('Cena s DPH') },
              ]}
              rows={invoice.items || []}
              renderRow={item => [
                item.name,
                item.quantity,
                formatPrice(
                  item.totalPrice.amount_vat_excl,
                  item.totalPrice.currency,
                ),
                formatPrice(item.totalPrice.amount, item.totalPrice.currency),
              ]}
              rowKey={(_item, index) => `item-${index}`}
            />

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                marginTop: 8,
              }}
            >
              <div>
                {__('Celková suma')}:{' '}
                {formatPrice(getInvoiceTotal(invoice), 'EUR')}
              </div>
              <strong style={{ fontSize: 16 }}>
                {__('K úhrade')}:{' '}
                {formatPrice(getInvoiceAmountToPay(invoice), 'EUR')}
              </strong>
            </div>

            <div style={{ marginBottom: 16 }} />

            {invoice.exports && Object.keys(invoice.exports).length > 0 && (
              <Segment
                raised
                header={
                  <SegmentHeader size="xs">{__('Exporty')}</SegmentHeader>
                }
              >
                {Object.entries(invoice.exports).map(
                  ([service, exportRows]) => (
                    <div key={service} style={{ marginBottom: 16 }}>
                      <strong style={{ textTransform: 'uppercase' }}>
                        {service}
                      </strong>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          marginTop: 4,
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                textAlign: 'left',
                                padding: '6px 8px',
                                borderBottom: '1px solid #eee',
                                fontSize: 13,
                                fontWeight: 500,
                              }}
                            >
                              {__('Dátum')}
                            </th>
                            <th
                              style={{
                                textAlign: 'left',
                                padding: '6px 8px',
                                borderBottom: '1px solid #eee',
                                fontSize: 13,
                                fontWeight: 500,
                              }}
                            >
                              {__('Stav')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(exportRows || []).map((exportRow, index) => {
                            const errorKey = `${service}-${index}`;
                            const isExpanded = expandedErrors[errorKey];
                            const hasError =
                              exportRow.status !== 'ok' && exportRow.error;
                            return (
                              <React.Fragment key={errorKey}>
                                <tr
                                  style={{
                                    cursor: hasError ? 'pointer' : 'default',
                                  }}
                                  onClick={
                                    hasError
                                      ? () => toggleError(errorKey)
                                      : undefined
                                  }
                                >
                                  <td
                                    style={{
                                      padding: '6px 8px',
                                      borderBottom: isExpanded
                                        ? 'none'
                                        : '1px solid #eee',
                                      fontSize: 13,
                                    }}
                                  >
                                    {exportRow.exportedDT
                                      ? moment(exportRow.exportedDT).format(
                                          'DD.MM.YYYY HH:mm:ss',
                                        )
                                      : '-'}
                                  </td>
                                  <td
                                    style={{
                                      padding: '6px 8px',
                                      borderBottom: isExpanded
                                        ? 'none'
                                        : '1px solid #eee',
                                      fontSize: 13,
                                    }}
                                  >
                                    {exportRow.status === 'ok' ? (
                                      <span style={{ color: 'green' }}>OK</span>
                                    ) : (
                                      <span style={{ color: 'red' }}>
                                        {__('Chyba')}
                                        {hasError && (isExpanded ? ' ▲' : ' ▼')}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                                {isExpanded && hasError && (
                                  <tr>
                                    <td
                                      colSpan={2}
                                      style={{
                                        padding: '0 8px 6px',
                                        borderBottom: '1px solid #eee',
                                      }}
                                    >
                                      <pre
                                        style={{
                                          margin: 0,
                                          padding: 8,
                                          background: '#f5f5f5',
                                          borderRadius: 4,
                                          fontSize: 12,
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {JSON.stringify(
                                          exportRow.error,
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ),
                )}
              </Segment>
            )}

            {activeConnections.length > 0 && (
              <Segment
                raised
                header={
                  <SegmentHeader size="xs">
                    {__('Exportovať do externého systému')}
                  </SegmentHeader>
                }
              >
                {activeConnections.map(conn => (
                  <Button
                    key={conn.type}
                    primary
                    loading={exporting === conn.type}
                    disabled={!!exporting}
                    onClick={() => handleExport(conn.type)}
                    style={{ marginRight: 8 }}
                  >
                    {__('Exportovať do')} {conn.name || conn.type}
                  </Button>
                ))}
              </Segment>
            )}
          </>
        )}
      </ModalContent>

      <ModalActions>
        <Button onClick={handleClose}>{__('Zavrieť')}</Button>
        {invoice && (
          <Button
            primary
            icon="file-download"
            loading={downloading}
            disabled={downloading}
            onClick={handleDownload}
          >
            {__('Stiahnuť faktúru')}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
};

export default InvoiceDetailModal;
